/**
 * The combobox's pure keyboard/filter logic (Story 2.6, spec judgement call
 * 4). No direct Radix primitive exists for a combobox — this is composed
 * from `Popover` plus a plain listbox in `components/ui/Combobox.tsx`,
 * governed by the same WAI-ARIA "combobox with list autocomplete" pattern
 * the four Radix-based primitives follow.
 *
 * Kept pure and DOM-free, mirroring `lib/list-query.ts`/`lib/inline-edit.ts`'s
 * own convention, so it is testable without a browser: this codebase has no
 * `jsdom`/`@testing-library` dependency (`DataTable.test.tsx`'s own header
 * comment explains why — adding either would be a new pinned dependency
 * this story's own rules also forbid), so a component that needs real
 * keyboard-event interaction to prove correct has to put that logic
 * somewhere a plain Vitest run — no DOM at all — can exercise it directly.
 * This is judgement call 4's "proven synthetically" half; the discharge
 * record is explicit that no real consumer exists in the app yet.
 */
export interface ComboboxOption {
  value: string;
  label: string;
}

/** Case-insensitive substring match on the option's label — the same
 * matching rule R-S2's future search overlay needs (EXPERIENCE.md:
 * "substring, case-insensitive"), proven here first. An empty query
 * matches everything, so opening the list with nothing typed shows the
 * full option set. */
export function filterOptions(options: ComboboxOption[], query: string): ComboboxOption[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return options;
  }
  return options.filter((option) => option.label.toLowerCase().includes(needle));
}

export type HighlightDirection = "next" | "previous" | "first" | "last";

/**
 * One WAI-ARIA Authoring Practices keyboard step: `ArrowDown`/`ArrowUp`
 * move the highlight and wrap at either boundary rather than dead-ending,
 * `Home`/`End`-equivalent jumps go straight to an end. Returns `null` when
 * `options` is empty — nothing to highlight. Starting from `null` (nothing
 * highlighted yet, e.g. the list has just opened) lands on the first
 * option going forward and the last one going backward, so a single
 * `ArrowUp` on a freshly opened list reaches the bottom directly, matching
 * the Authoring Practices' own "if nothing is selected, moving up selects
 * the last option" rule.
 */
export function moveHighlight(
  options: ComboboxOption[],
  currentIndex: number | null,
  direction: HighlightDirection,
): number | null {
  if (options.length === 0) {
    return null;
  }
  const lastIndex = options.length - 1;
  if (direction === "first") {
    return 0;
  }
  if (direction === "last") {
    return lastIndex;
  }
  if (currentIndex === null) {
    return direction === "next" ? 0 : lastIndex;
  }
  if (direction === "next") {
    return currentIndex >= lastIndex ? 0 : currentIndex + 1;
  }
  return currentIndex <= 0 ? lastIndex : currentIndex - 1;
}

/** The option at `index`, or `undefined` for `null`/an out-of-range index
 * (an empty or since-filtered-shorter list). */
export function optionAt(options: ComboboxOption[], index: number | null): ComboboxOption | undefined {
  if (index === null) {
    return undefined;
  }
  return options[index];
}
